import React, { useEffect, useRef } from 'react';
import Header from '../components/Header';
import './TownBudgets.css';

const DATA = [
  { town: "Brookline",  pop: 63266, budget: 408222486, budgetSource: "https://stories.opengov.com/brooklinema/5cf061e0-65c6-428e-be0f-fee5a4848e8f/published/mpRiIYj5M?currentPageId=688a336d60c0045dfa08f22f" },
  { town: "Newton",     pop: 89044, budget: 547629106, budgetSource: "https://www.newtonma.gov/government/comptroller/budget" },
  { town: "Lexington",  pop: 34295, budget: 311154209, budgetSource: "https://www.lexingtonma.gov/2250/FY2026-Budget" },
  { town: "Arlington",  pop: 46350, budget: 226495721, budgetSource: "https://www.arlingtonma.gov/departments/town-manager/town-manager-s-annual-budget-financial-report" },
  { town: "Natick",     pop: 36873, budget: 208652635, budgetSource: "https://natickma.gov/2190/FY26-Budget-Override-ARCHIVED" },
  { town: "Watertown",  pop: 35543, budget: 223660433, budgetSource: "https://www.watertown-ma.gov/277/Annual-TownCity-Reports" },
  { town: "Needham",    pop: 32459, budget: 256934083, budgetSource: "https://www.needhamma.gov/5632/FY2026-Budget-Documents" },
  { town: "Wellesley",  pop: 30347, budget: 224493903, budgetSource: "https://wellesleyma.gov/2468/2026-Annual-Town-Meeting-Budget-Book" },
  { town: "Milton",     pop: 28587, budget: 149325163, budgetSource: "https://www.miltonma.gov/1139/Budget-Information" },
  { town: "Belmont",    pop: 27175, budget: 166230000, budgetSource: "https://www.belmont-ma.gov/DocumentCenter/View/9065/FY2026---Final-Budget-Recommendation---3-21-2025" },
].sort((a, b) => b.pop - a.pop).map(d => ({
  ...d,
  budgetM: +(d.budget / 1e6).toFixed(1),
  perCapita: Math.round(d.budget / d.pop),
}));

const PENSION_DATA = [
  { town: "Brookline",  uaal: 167626255, valDate: "Jan 1, 2024" },
  { town: "Newton",     uaal: 277775855, valDate: "Jan 1, 2025" },
  { town: "Lexington",  uaal: 47725644,  valDate: "Jan 1, 2023" },
  { town: "Arlington",  uaal: 112162710, valDate: "Jan 1, 2024" },
  { town: "Natick",     uaal: 75400000,  valDate: "Jan 1, 2024" },
  { town: "Watertown",  uaal: 5922657,   valDate: "Jan 1, 2025" },
  { town: "Needham",    uaal: 70635100,  valDate: "Jan 1, 2024" },
  { town: "Wellesley",  uaal: 22090768,  valDate: "Jan 1, 2025" },
  { town: "Milton",     uaal: -7204030,  valDate: "Jan 1, 2025" },
  { town: "Belmont",    uaal: 53640886,  valDate: "Jan 1, 2024" },
].map(d => {
  const match = DATA.find(t => t.town === d.town);
  return { ...d, pop: match ? match.pop : 0, uaalPerCapita: match ? Math.round(d.uaal / match.pop) : 0 };
});

const BROOKLINE_COLOR = '#2B6CB0';
const OTHER_COLOR = '#90CDF4';
const POP_COLOR = '#E2E8F0';

function TownBudgets() {
  const dualRef = useRef(null);
  const perCapitaRef = useRef(null);
  const scatterRef = useRef(null);
  const pensionRef = useRef(null);
  const chartsRef = useRef([]);

  useEffect(() => {
    document.title = 'Town Budgets vs. Population — Brookline';
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadChartJs = async () => {
      if (!window.Chart) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.min.js';
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }
      if (cancelled) return;
      renderCharts();
    };

    loadChartJs();

    return () => {
      cancelled = true;
      chartsRef.current.forEach(c => c.destroy());
      chartsRef.current = [];
    };
  }, []);

  const renderCharts = () => {
    const Chart = window.Chart;
    const colors = DATA.map(d => d.town === "Brookline" ? BROOKLINE_COLOR : OTHER_COLOR);
    const popColors = DATA.map(d => d.town === "Brookline" ? '#A0AEC0' : POP_COLOR);

    // Chart 1: Dual-axis budget vs population
    chartsRef.current.push(new Chart(dualRef.current.getContext('2d'), {
      type: 'bar',
      data: {
        labels: DATA.map(d => d.town),
        datasets: [
          {
            label: 'Population',
            data: DATA.map(d => d.pop),
            backgroundColor: popColors,
            borderRadius: 4,
            yAxisID: 'yPop',
            order: 2
          },
          {
            label: 'Total Budget ($M)',
            data: DATA.map(d => d.budgetM),
            backgroundColor: colors,
            borderRadius: 4,
            yAxisID: 'yBudget',
            order: 1
          }
        ]
      },
      options: {
        responsive: true,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          tooltip: {
            callbacks: {
              label: ctx => {
                if (ctx.dataset.yAxisID === 'yBudget') return ctx.dataset.label + ': $' + ctx.raw + 'M';
                return ctx.dataset.label + ': ' + ctx.raw.toLocaleString();
              }
            }
          },
          legend: { position: 'top' }
        },
        scales: {
          yPop: { type: 'linear', position: 'left', title: { display: true, text: 'Population' }, grid: { drawOnChartArea: true }, ticks: { callback: v => v.toLocaleString() } },
          yBudget: { type: 'linear', position: 'right', title: { display: true, text: 'Budget ($M)' }, grid: { drawOnChartArea: false }, ticks: { callback: v => '$' + v + 'M' } }
        }
      }
    }));

    // Chart 2: Budget per capita (horizontal bar)
    const pcSorted = [...DATA].sort((a, b) => b.perCapita - a.perCapita);
    chartsRef.current.push(new Chart(perCapitaRef.current.getContext('2d'), {
      type: 'bar',
      data: {
        labels: pcSorted.map(d => d.town),
        datasets: [{
          label: 'Budget per Capita',
          data: pcSorted.map(d => d.perCapita),
          backgroundColor: pcSorted.map(d => d.town === "Brookline" ? BROOKLINE_COLOR : OTHER_COLOR),
          borderRadius: 4
        }]
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: { label: ctx => '$' + ctx.raw.toLocaleString() + ' per resident' } }
        },
        scales: { x: { title: { display: true, text: 'Budget per Capita ($)' }, beginAtZero: true, ticks: { callback: v => '$' + v.toLocaleString() } } }
      }
    }));

    // Chart 3: Pension UAAL per capita (horizontal bar)
    const pensionSorted = [...PENSION_DATA].sort((a, b) => b.uaalPerCapita - a.uaalPerCapita);
    chartsRef.current.push(new Chart(pensionRef.current.getContext('2d'), {
      type: 'bar',
      data: {
        labels: pensionSorted.map(d => d.town),
        datasets: [{
          label: 'Pension Liability per Capita',
          data: pensionSorted.map(d => d.uaalPerCapita),
          backgroundColor: pensionSorted.map(d => {
            if (d.town === "Brookline") return BROOKLINE_COLOR;
            if (d.uaalPerCapita < 0) return '#48BB78';
            return OTHER_COLOR;
          }),
          borderRadius: 4
        }]
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: { label: ctx => {
            const val = ctx.raw;
            if (val < 0) return 'Overfunded: $' + Math.abs(val).toLocaleString() + ' per resident';
            return '$' + val.toLocaleString() + ' per resident';
          }}}
        },
        scales: { x: { title: { display: true, text: 'UAAL per Capita ($)' }, ticks: { callback: v => '$' + v.toLocaleString() } } }
      }
    }));

    // Chart 4: Scatter — population vs budget
    chartsRef.current.push(new Chart(scatterRef.current.getContext('2d'), {
      type: 'scatter',
      data: {
        datasets: [{
          label: 'Towns',
          data: DATA.map(d => ({ x: d.pop, y: d.budgetM, town: d.town })),
          backgroundColor: DATA.map(d => d.town === "Brookline" ? BROOKLINE_COLOR : OTHER_COLOR),
          pointRadius: DATA.map(d => d.town === "Brookline" ? 10 : 7),
          pointHoverRadius: 12
        }]
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: ctx => {
                const d = ctx.raw;
                return d.town + ': Pop ' + d.x.toLocaleString() + ', Budget $' + d.y + 'M';
              }
            }
          }
        },
        scales: {
          x: { title: { display: true, text: 'Population' }, ticks: { callback: v => v.toLocaleString() } },
          y: { title: { display: true, text: 'Total Budget ($M)' }, ticks: { callback: v => '$' + v + 'M' } }
        }
      }
    }));
  };

  const tableData = [...DATA].sort((a, b) => b.perCapita - a.perCapita);

  return (
    <div className="town-budgets-page">
      <Header
        title="Town Budgets vs. Population"
        subtitle="Brookline & Comparable MA Communities"
      />

      <div className="town-budgets-content">
        <p className="tb-subtitle">FY2026 operating budgets (excluding enterprise funds) &bull; 2024 ACS population estimates</p>

        <div className="charts-grid">
          <div className="card">
            <h2>Total Budget and Town Population</h2>
            <div className="chart-wrap"><canvas ref={dualRef} height="340"></canvas></div>
          </div>

          <div className="card">
            <h2>Budget per Capita</h2>
            <div className="chart-wrap"><canvas ref={perCapitaRef} height="300"></canvas></div>
          </div>

          <div className="card">
            <h2>Pension Liability (UAAL) per Capita</h2>
            <div className="chart-wrap"><canvas ref={pensionRef} height="300"></canvas></div>
          </div>

          <div className="card">
            <h2>Pension Liability Data</h2>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Community</th>
                    <th>UAAL</th>
                    <th>UAAL per Capita</th>
                    <th>Valuation Date</th>
                  </tr>
                </thead>
                <tbody>
                  {[...PENSION_DATA].sort((a, b) => b.uaalPerCapita - a.uaalPerCapita).map(d => (
                    <tr key={d.town} className={d.town === 'Brookline' ? 'highlight' : ''}>
                      <td>{d.town}</td>
                      <td>{d.uaal < 0 ? '($' + Math.abs(d.uaal).toLocaleString() + ')' : '$' + d.uaal.toLocaleString()}</td>
                      <td>{d.uaalPerCapita < 0 ? '($' + Math.abs(d.uaalPerCapita).toLocaleString() + ')' : '$' + d.uaalPerCapita.toLocaleString()}</td>
                      <td>{d.valDate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="note">
              <strong>Note on data comparability:</strong> UAAL (Unfunded Actuarial Accrued Liability) figures are on an Actuarial Value of Assets (AVA) basis, sourced from each town's most recent PERAC actuarial valuation report. Valuation dates range from Jan 1, 2023 (Lexington) to Jan 1, 2025. Negative values indicate the pension system is overfunded. Different actuaries and assumptions may affect comparability across towns.
            </div>
          </div>

          <div className="card">
            <h2>Population vs. Budget</h2>
            <div className="chart-wrap"><canvas ref={scatterRef} height="340"></canvas></div>
          </div>

          <div className="card">
            <h2>Underlying Data</h2>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Community</th>
                    <th>Population (2024)</th>
                    <th>Total Budget (FY2026)</th>
                    <th>Budget per Capita</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map(d => (
                    <tr key={d.town} className={d.town === 'Brookline' ? 'highlight' : ''}>
                      <td>{d.budgetSource ? <a href={d.budgetSource} target="_blank" rel="noopener noreferrer">{d.town}</a> : d.town}</td>
                      <td>{d.pop.toLocaleString()}</td>
                      <td>${d.budget.toLocaleString()}</td>
                      <td>${d.perCapita.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="note">
              <strong>Note on data comparability:</strong> Budget figures are FY2026 operating budgets excluding enterprise funds, sourced from each town's official budget documents. Population data is from the 2024 American Community Survey.
            </div>
          </div>
        </div>

        <p className="source">
          <strong>Sources:</strong>{' '}
          Budget data from individual town FY2026 budget documents (click town names in the table above for sources).{' '}
          Population from <a href="https://www.massachusetts-demographics.com/cities_by_population" target="_blank" rel="noopener noreferrer">2024 American Community Survey</a>.{' '}
          Pension data from <a href="https://www.mass.gov/public-employee-retirement-administration-commission-perac" target="_blank" rel="noopener noreferrer">PERAC actuarial valuation reports</a>.
        </p>
      </div>
    </div>
  );
}

export default TownBudgets;
